import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { interval, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

import { ClassesEntity } from '../../../../../../models/entities/classes-entity';
import { IClassesEntityService } from '../../../../../../interfaces/entities/classes-entity-service.interface';
import { CLASSES_ENTITY_SERVICE_TOKEN } from '../../../../../../injection-tokens/entity-service-tokens';
import { Log } from '../../../../../../services/logger.service';
import { NotificationService } from '../../../../../../services/notification.service';
import { UnifiedVmLauncherService } from '../../../../../../services/unified-vm-launcher.service';
import { ResourceCreatorService } from '../../../../../../services/resource-creator.service';
import { ResourceDetailsResponse } from '../../../../../../interfaces/resource-details.interface';
import { ResourceMasterDataService } from '../../../../../../services/resource-master-data.service';
import { SoftwareFilterService } from '../../../../../../services/software-filter.service';
import { InstallationSummary, TemplateInfoCardData } from '../../../../../shared/template-info-card/template-info-card.component';
import { DrawerCategoryGroup } from '../../../../../../services/drawer.service';

/** Union of all possible VM template provisioning states returned by the API. */
export type VmTemplateState =
  | 'NOT_STARTED'
  | 'PENDING'
  | 'IN_PROGRESS'
  | 'CREATED'
  | 'FAILED'
  | 'SNAPSHOTTING'
  | 'SNAPSHOTTED';

const TERMINAL_STATES: VmTemplateState[] = ['CREATED', 'FAILED', 'SNAPSHOTTED'];
const POLL_INTERVAL_MS = 5000;

@Component({
  selector: 'app-vm-template-settings',
  templateUrl: './vm-template-settings.component.html',
  styleUrls: ['./vm-template-settings.component.css'],
})
export class VmTemplateSettingsComponent implements OnChanges, OnDestroy {
  @Input() classId!: string;
  @Input() classEntity: ClassesEntity | null = null;
  /**
   * Pass `true` when the current user has cloud-settings / admin rights so that
   * the "Testing Done" (snapshot) button is enabled.
   */
  @Input() hasCloudSettings = false;

  /** Emitted once when the snapshot succeeds — the parent should set `hasSnapshot = true`. */
  @Output() snapshotDone = new EventEmitter<void>();
  /** Emitted when the user clicks "Go to VM Subscriptions" in the no-subscription blocked state. */
  @Output() goToVmSubscriptions = new EventEmitter<void>();

  vmState: VmTemplateState = 'NOT_STARTED';
  logs: string[] = [];
  actionError: string | null = null;
  isActioning = false;
  isLoadingStatus = false;
  isLaunching = false;
  isLoadingMeta = false;
  resourceDetails: ResourceDetailsResponse | null = null;
  isConfirmingReused = false;
  /** Optimistic flag set right after a successful `confirmReusedTemplate()` call, so the UI
   *  flips immediately without waiting for the parent's classEntity re-fetch round trip. */
  private confirmedReusedLocally = false;

  private readonly userId = localStorage.getItem('userId') ?? '';
  private readonly userName = localStorage.getItem('userName') ?? '';
  private readonly userRole = localStorage.getItem('userRole') ?? 'trainer';

  private readonly destroy$ = new Subject<void>();
  private readonly stopPolling$ = new Subject<void>();
  private readonly logger = Log.create(VmTemplateSettingsComponent);

  constructor(
    @Inject(CLASSES_ENTITY_SERVICE_TOKEN) private readonly classService: IClassesEntityService,
    private readonly unifiedLauncher: UnifiedVmLauncherService,
    private readonly resourceCreatorService: ResourceCreatorService,
    private readonly masterData: ResourceMasterDataService,
    private readonly softwareFilterService: SoftwareFilterService,
    private readonly notification: NotificationService,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['classId'] && this.classId) {
      this.stopPolling$.next();
      this.logs = [];
      this.actionError = null;
      this.confirmedReusedLocally = false;
    }
    if (changes['classEntity'] && this.classEntity) {
      this.loadResourceDetails();
    }
    // Reload status whenever either input changes, as we need both classId and classEntity
    if ((changes['classId'] || changes['classEntity']) && this.classId && this.classEntity) {
      this.loadStatus();
    }
  }

  private loadResourceDetails(): void {
    const templates: any[] = (this.classEntity as any)?.trainingSchedule?.resourceTemplateIds ?? [];
    const vmTpl = templates.find(
      (t: any) => (t?.resourceType ?? t?.type ?? '').toLowerCase() === 'vm',
    );
    const templateId: string | null = vmTpl?.id ?? vmTpl?.Id ?? null;
    if (!templateId) return;

    this.isLoadingMeta = true;
    this.resourceCreatorService.getResource(templateId).subscribe({
      next: (details: ResourceDetailsResponse) => {
        this.resourceDetails = details;
        this.isLoadingMeta = false;
      },
      error: (err: any) => {
        this.isLoadingMeta = false;
        this.logger.e('Failed to load resource template details:', err);
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---- Status ----

  private getTemplateContext(): { templateId: string; customerId: string; classStage: string } | null {
    const templates: any[] = (this.classEntity as any)?.trainingSchedule?.resourceTemplateIds ?? [];
    const vmTpl = templates.find((t: any) => (t?.resourceType ?? t?.type ?? '').toLowerCase() === 'vm');
    const templateId: string = vmTpl?.id ?? vmTpl?.Id ?? '';
    if (!templateId) return null;
    const customerId: string = (this.classEntity as any)?.customer?.id ?? '';
    // Note: customerId may be empty for in-house classes (no customer assigned).
    // In that case the backend will look for CustomerStatuses[""] rather than falling
    // to the org-level branch.  We no longer gate on customerId being present here.
    const classStage: string = (this.classEntity as any)?.stage ?? (this.classEntity as any)?.Stage ?? '';
    return { templateId, customerId, classStage };
  }

  /**
   * True once THIS class's own VM entry in `resourceTemplateIds` is marked "tested" server-side
   * by `MarkTestedAsync` — distinct from `vmState === 'SNAPSHOTTED'`, which reflects the
   * Azure/customer-level snapshot record shared across every class scoped to the same
   * (CustomerId, TenantSecretId) pair. When a class reuses another class's template
   * (see the reuse check in `CreateTemplateVMForClass`), the shared record is already
   * "completed" and `vmState` is `SNAPSHOTTED` immediately — but this class was never itself
   * marked tested, so the Forward-tab gate (`settings-tab.component.ts` `isVmTested`) still
   * blocks it. Use this getter to tell the two SNAPSHOTTED cases apart in the template.
   */
  get isTemplateTestedForClass(): boolean {
    if (this.confirmedReusedLocally) return true;
    const templates: any[] = (this.classEntity as any)?.trainingSchedule?.resourceTemplateIds ?? [];
    const vm = templates.find((t: any) => (t?.resourceType ?? t?.type ?? '').toLowerCase() === 'vm');
    return (vm?.status ?? vm?.Status ?? '').toLowerCase() === 'tested';
  }

  private loadStatus(): void {
    const ctx = this.getTemplateContext();
    if (!ctx) return;
    this.isLoadingStatus = true;
    this.classService.getVmTemplateStatus(ctx.templateId, ctx.customerId, ctx.classStage, this.classId).subscribe({
      next: (res) => {
        this.isLoadingStatus = false;
        const state = (res.state?.toUpperCase() as VmTemplateState) ?? 'NOT_STARTED';
        this.vmState = state;
        if (res.logs) this.logs = res.logs;
        if (!TERMINAL_STATES.includes(state)) {
          this.startPolling();
        }
      },
      error: (err: any) => {
        this.isLoadingStatus = false;
        this.logger.e('Failed to load VM template status:', err);
        this.vmState = 'NOT_STARTED';
      },
    });
  }

  private startPolling(): void {
    const ctx = this.getTemplateContext();
    if (!ctx) return;
    this.stopPolling$.next();
    interval(POLL_INTERVAL_MS)
      .pipe(
        takeUntil(this.stopPolling$),
        takeUntil(this.destroy$),
        switchMap(() => this.classService.getVmTemplateStatus(ctx.templateId, ctx.customerId, ctx.classStage, this.classId)),
      )
      .subscribe({
        next: (res) => {
          const state = (res.state?.toUpperCase() as VmTemplateState) ?? this.vmState;
          this.vmState = state;
          if (res.logs) this.logs = res.logs;
          if (TERMINAL_STATES.includes(state)) {
            this.stopPolling$.next();
            if (state === 'SNAPSHOTTED') {
              this.snapshotDone.emit();
            }
          }
        },
        error: (err: any) => this.logger.e('Status poll error:', err),
      });
  }

  // ---- Actions ----

  createTemplate(): void {
    if (!this.hasVmSubscriptions) return;
    this.actionError = null;
    this.isActioning = true;
    this.classService.createVmTemplate(this.classId).subscribe({
      next: () => {
        this.isActioning = false;
        this.vmState = 'PENDING';
        this.startPolling();
        this.logger.i('VM template creation triggered.');
      },
      error: (err: any) => {
        this.actionError = err?.error?.message ?? 'Failed to trigger template creation.';
        this.isActioning = false;
        this.logger.e('Create template error:', err);
      },
    });
  }

  markTestingDone(): void {
    if (!this.hasCloudSettings) return;
    const ctx = this.getTemplateContext();
    if (!ctx) {
      this.actionError = 'No VM template found for this class.';
      return;
    }
    this.actionError = null;
    this.isActioning = true;
    // Backend returns 202 immediately — snapshot runs in background.
    // Transition to SNAPSHOTTING and poll until completed or snapshot_failed.
    this.classService.markTemplateTested(this.classId, ctx.templateId, 'vm').subscribe({
      next: () => {
        this.isActioning = false;
        this.vmState = 'SNAPSHOTTING';
        this.startPolling();
        this.logger.i('VM template mark-tested accepted — polling for completion.');
      },
      error: (err: any) => {
        this.actionError = err?.error?.message ?? 'Failed to initiate snapshot.';
        this.isActioning = false;
        this.logger.e('Mark VM template tested error:', err);
      },
    });
  }

  /**
   * "Confirm Class" handler for the reused-template case: `vmState === 'SNAPSHOTTED'` (shared
   * Azure snapshot exists for this customer+tenant) but `isTemplateTestedForClass` is false
   * (this class itself was never marked tested, since the reuse path skips provisioning and
   * never calls MarkTestedAsync). Calls the same `mark-tested` endpoint as "Testing Done" —
   * `VmTemplateTestingService.MarkTestedAsync` detects the snapshot is already "completed" and
   * advances straight to `CompleteTestingAsync` (sets this class's own tested status and
   * attempts the class auto-confirm CAS), no new snapshot is created.
   */
  confirmReusedTemplate(): void {
    if (!this.hasCloudSettings || !this.hasVmSubscriptions) return;
    const ctx = this.getTemplateContext();
    if (!ctx) {
      this.actionError = 'No VM template found for this class.';
      return;
    }
    this.actionError = null;
    this.isConfirmingReused = true;
    this.classService.markTemplateTested(this.classId, ctx.templateId, 'vm').subscribe({
      next: () => {
        this.isConfirmingReused = false;
        this.confirmedReusedLocally = true;
        this.snapshotDone.emit();
        this.logger.i('Reused template confirmed for class.');
      },
      error: (err: any) => {
        this.actionError = err?.error?.message ?? 'Failed to confirm class.';
        this.isConfirmingReused = false;
        this.logger.e('Confirm reused template error:', err);
      },
    });
  }

  cancelProvisioning(): void {
    this.actionError = null;
    this.isActioning = true;
    this.classService.cancelVmTemplate(this.classId).subscribe({
      next: () => {
        this.isActioning = false;
        this.vmState = 'NOT_STARTED';
        this.stopPolling$.next();
        this.logs = [];
        this.logger.i('VM template provisioning cancelled.');
      },
      error: (err: any) => {
        this.actionError = err?.error?.message ?? 'Failed to cancel.';
        this.isActioning = false;
        this.logger.e('Cancel error:', err);
      },
    });
  }

  retryProvisioning(): void {
    this.logs = [];
    this.vmState = 'NOT_STARTED';
    this.createTemplate();
  }

  // ---- Template metadata ----

  /** Extracts display properties from the loaded resource template document for the info card. */
  get templateMeta(): {
    name: string;
    provider: string;
    vmSize: string;
    region: string;
    storage: string;
    osLabel: string;
    sdks: string[];
    editors: string[];
    tools: string[];
    databases: string[];
  } {
    const templates: any[] = (this.classEntity as any)?.trainingSchedule?.resourceTemplateIds ?? [];
    const vmTpl = templates.find(
      (t: any) => (t?.resourceType ?? t?.type ?? '').toLowerCase() === 'vm',
    );
    const cpd = this.resourceDetails?.data?.CloudProviderData;
    const sd = this.resourceDetails?.data?.SoftwareData;

    return {
      name: vmTpl?.name ?? vmTpl?.Name ?? '—',
      provider: this.resolveProviderName(cpd?.provider),
      vmSize: this.resolveSizeName(cpd?.provider, cpd?.size),
      region: this.resolveRegionName(cpd?.provider, cpd?.region),
      storage: this.resolveStorageName(cpd?.provider, cpd?.storage),
      osLabel: this.resolveOsName(sd?.os, sd?.osVersion),
      sdks: this.resolveItemNames('software.sdks', sd?.sdks ?? []),
      editors: this.resolveItemNames('software.editors', sd?.editors ?? []),
      tools: this.resolveItemNames('software.tools', sd?.misc ?? []),
      databases: this.resolveItemNames('software.databases', sd?.databases ?? []),
    };
  }

  // ── Master-data label resolvers ──────────────────────────────────────────

  private resolveProviderName(provider: string | undefined): string {
    if (!provider) return '—';
    return this.masterData.get<any>(`cloud.${provider.toLowerCase()}`)?.name ?? provider;
  }

  private resolveRegionName(provider: string | undefined, regionId: string | undefined): string {
    if (!regionId) return '—';
    const provKey = (provider ?? 'azure').toLowerCase();
    const regions: any[] = this.masterData.get<any[]>(`cloud.${provKey}.regions`) ?? [];
    return regions.find(r => r.id === regionId)?.name ?? regionId;
  }

  private resolveSizeName(provider: string | undefined, sizeId: string | undefined): string {
    if (!sizeId) return '—';
    const provKey = (provider ?? 'azure').toLowerCase();
    const sizes: any[] = this.masterData.get<any[]>(`cloud.${provKey}.sizes`) ?? [];
    return sizes.find(s => s.id === sizeId)?.name ?? sizeId;
  }

  private resolveStorageName(provider: string | undefined, storageId: string | undefined): string {
    if (!storageId) return '—';
    const provKey = (provider ?? 'azure').toLowerCase();
    const storages: any[] = this.masterData.get<any[]>(`cloud.${provKey}.storage`) ?? [];
    return storages.find(s => s.id === storageId)?.name ?? storageId;
  }

  private resolveOsName(os: string | undefined, osVersion: string | undefined): string {
    if (!os && !osVersion) return '—';
    const osFamilies = Object.keys(this.masterData.get<any>('software.os') ?? {});
    for (const family of osFamilies) {
      const versions: any[] = this.masterData.get<any[]>(`software.os.${family}.versions`) ?? [];
      const match = versions.find(
        v => v.id === osVersion || v.id === `${os}_${osVersion}`,
      );
      if (match) return match.name;
    }
    return osVersion ? `${os} ${osVersion}`.trim() : (os ?? '—');
  }

  private resolveItemNames(listPath: string, ids: string[]): string[] {
    if (!ids?.length) return [];
    const list: any[] = this.masterData.get<any[]>(listPath) ?? [];
    return ids.map(id => list.find(x => x.id === id)?.name ?? id);
  }

  /** True when this class has at least one VM subscription configured.
   * Used to gate the "Create Template VM" button — no sub = blocked state shown instead. */
  get hasVmSubscriptions(): boolean {
    const raw: any[] = this.classEntity?.data?.['VMSubscription']
      ?? this.classEntity?.data?.['VmSubscription']
      ?? [];
    return raw.length > 0;
  }

  /** Human-readable label for the current provisioning state. */
  get vmStateLabel(): string {
    const labels: Record<VmTemplateState, string> = {
      NOT_STARTED: 'Not Started',
      PENDING: 'Queued',
      IN_PROGRESS: 'In Progress',
      CREATED: 'VM Ready',
      FAILED: 'Failed',
      SNAPSHOTTING: 'Snapshotting…',
      SNAPSHOTTED: 'Confirmed',
    };
    return labels[this.vmState] ?? this.vmState;
  }

  /**
   * Maps vm-template-settings status vocabulary to the resource-cards vocabulary
   * consumed by {@link TemplateInfoCardComponent}.
   * Dictionary lookup — no switch/if-else ladder.
   */
  private readonly vmStateToStatusMap: Record<VmTemplateState, string> = {
    NOT_STARTED: 'draft',
    PENDING: 'creating',
    IN_PROGRESS: 'creating',
    SNAPSHOTTING: 'creating',
    CREATED: 'active',
    FAILED: 'failed',
    SNAPSHOTTED: 'snapshotted',
  };

  /** Maps installation summary status keys to clean display labels (no emoji). */
  private readonly installationSummaryLabels: Record<string, string> = {
    failed: 'Some software failed to install',
    pending: 'Installation in progress',
    installed: 'All software installed successfully',
  };

  /**
   * Derives the structured {@link InstallationSummary} from the loaded resource details.
   * Uses the class customer ID as the lookup key for `softwareInstallationStatuses`,
   * matching the scoped install records written during class-template provisioning.
   * Returns undefined when no status data is present.
   */
  private get installationSummary(): InstallationSummary | undefined {
    const customerId: string = (this.classEntity as any)?.customer?.id ?? '';
    const statusMap = this.resourceDetails?.softwareInstallationStatuses?.[customerId];
    if (!statusMap || Object.keys(statusMap).length === 0) return undefined;

    let hasFailed = false;
    let hasPending = false;

    for (const cat of Object.values(statusMap)) {
      for (const entry of Object.values(cat)) {
        const s = entry.status;
        if (s === 'failed') hasFailed = true;
        if (s === 'pending') hasPending = true;
      }
    }

    const key = hasFailed ? 'failed' : hasPending ? 'pending' : 'installed';
    return {
      status: hasFailed ? 'failed' : hasPending ? 'pending' : 'installed',
      label: this.installationSummaryLabels[key],
    };
  }

  /**
   * Builds a {@link TemplateInfoCardData} for the shared template-info-card component,
   * mapping templateMeta fields + the current vmState to the component's data contract.
   */
  get templateInfoCardData(): TemplateInfoCardData {
    const meta = this.templateMeta;
    return {
      name: meta.name,
      provider: meta.provider,
      region: meta.region,
      vmSize: meta.vmSize,
      storage: meta.storage,
      osLabel: meta.osLabel,
      sdks: meta.sdks,
      editors: meta.editors,
      tools: meta.tools,
      databases: meta.databases,
      status: this.vmStateToStatusMap[this.vmState] ?? 'draft',
      installationSummary: this.installationSummary,
      isLoading: this.isLoadingMeta,
      cardTitle: 'Template Details',
      cardSubtitle: 'Configured resource template for this class',
    };
  }

  /** Generates a detailed tooltip showing all selected software stack items grouped by category. */
  generateSoftwareStackTooltip(): string {
    const parts: string[] = [];

    if (this.templateMeta.sdks?.length) {
      parts.push(`SDKs: ${this.templateMeta.sdks.join(', ')}`);
    }
    if (this.templateMeta.editors?.length) {
      parts.push(`Editors: ${this.templateMeta.editors.join(', ')}`);
    }
    if (this.templateMeta.tools?.length) {
      parts.push(`Tools: ${this.templateMeta.tools.join(', ')}`);
    }
    if (this.templateMeta.databases?.length) {
      parts.push(`Databases: ${this.templateMeta.databases.join(', ')}`);
    }

    return parts.length > 0 ? parts.join(' | ') : 'No software stack configured';
  }

  private get vmTemplateId(): string | null {
    const templates: any[] = (this.classEntity as any)?.trainingSchedule?.resourceTemplateIds ?? [];
    const vmTpl = templates.find((t: any) => (t?.resourceType ?? t?.type ?? '').toLowerCase() === 'vm');
    return vmTpl?.id ?? vmTpl?.Id ?? null;
  }

  private get accTemplateId(): string | null {
    const templates: any[] = (this.classEntity as any)?.trainingSchedule?.resourceTemplateIds ?? [];
    const tpl = templates.find(
      (t: any) => (t?.resourceType ?? t?.type ?? '').toLowerCase() === 'acc',
    );
    return tpl?.id ?? tpl?.Id ?? null;
  }

  /**
   * Builds the accordion content for the installation status drawer.
   * Reads `softwareInstallationStatuses` keyed by the class customer ID,
   * resolves human-readable software names via master data, and groups the
   * items into {@link DrawerCategoryGroup} entries expected by the shared drawer.
   * Applies OS-aware filtering to ensure only compatible software for the template's OS is shown.
   */
  get installationDrawerContent(): DrawerCategoryGroup[] {
    const customerId: string = (this.classEntity as any)?.customer?.id ?? '';
    const statusMap = this.resourceDetails?.softwareInstallationStatuses?.[customerId];
    if (!statusMap || Object.keys(statusMap).length === 0) return [];

    // Get OS info from template's software data for filtering
    const sd = this.resourceDetails?.data?.SoftwareData;
    const osType = sd?.os;
    const osVersion = sd?.osVersion;

    // Only apply filtering if both OS type and version are available
    if (!osType || !osVersion) return [];

    const categoryIcons: Record<string, string> = {
      sdks: 'code',
      databases: 'storage',
      editors: 'edit_note',
      misc: 'construction',
    };

    const categoryDisplayNames: Record<string, string> = {
      sdks: 'SDKs & Frameworks',
      databases: 'Databases',
      editors: 'IDE Editors',
      misc: 'Miscellaneous & Tools',
    };

    const categoryMasterKeys: Record<string, string> = {
      sdks: 'software.sdks',
      databases: 'software.databases',
      editors: 'software.editors',
      misc: 'software.misc',
    };

    const categoryOrder = ['sdks', 'databases', 'editors', 'misc'];

    return categoryOrder
      .filter(cat => statusMap[cat] && Object.keys(statusMap[cat]).length > 0)
      .map(cat => {
        const catEntries = statusMap[cat];
        const masterKey = categoryMasterKeys[cat] ?? '';
        const masterList = masterKey
          ? (this.masterData.get<{ id: string; name: string }[]>(masterKey) ?? [])
          : [];

        // Filter the master list by OS compatibility before building items
        const filteredMasterList = this.softwareFilterService.filterByOs(
          masterList,
          osType,
          osVersion
        );

        const items = Object.keys(catEntries)
          .map(softwareId => ({
            id: softwareId,
            name: filteredMasterList.find(x => x.id === softwareId)?.name ?? softwareId,
            status: catEntries[softwareId].status,
            error: catEntries[softwareId].error ?? '',
          }))
          // Only include items that exist in the filtered master list (compatible with OS)
          .filter(item => filteredMasterList.some(x => x.id === item.id))
          .sort((a, b) => a.name.localeCompare(b.name));

        return {
          category: categoryDisplayNames[cat] ?? cat,
          icon: categoryIcons[cat] ?? 'info',
          items,
        };
      });
  }

  launchTemplateVm(): void {
    const templateId = this.vmTemplateId;
    if (!templateId) {
      this.actionError = 'Template ID not found — cannot launch.';
      return;
    }
    const preOpenedWindow = window.open('', '_blank');
    this.unifiedLauncher.launchClassTemplateVm({
      userId: this.userId,
      templateId,
      classId: this.classId,
      userName: this.userName,
      vmConnectionTemplateId: this.attachedVmConnectionTemplateId,
      uaTemplateId: this.accTemplateId,
    }, preOpenedWindow);
  }

  // ── VM Connection Template attachment ───────────────────────────────────────

  /**
   * Whether the attached-template panel should be visible. Per round-3 feedback the panel
   * is **only** shown after the test VM is provisioned — pre-attaching saves no time and
   * may confuse trainers, so the gate matches the same predicate that controls the existing
   * "Connection Details" / "Launch Template VM" surfaces.
   */
  get isVmProvisioned(): boolean {
    return this.hasVmSubscriptions && (this.vmState === 'CREATED' || this.vmState === 'SNAPSHOTTED');
  }

  /** Reads the class's currently-attached VM Connection Template id (or null). */
  get attachedVmConnectionTemplateId(): string | null {
    return (this.classEntity as any)?.vmConnectionTemplateId
      ?? (this.classEntity as any)?.data?.['VmConnectionTemplateId']
      ?? null;
  }

  /** Tracks the in-flight save when the user changes the attachment. */
  isSavingVmConnectionTemplate = false;

  /**
   * Persists the user's attach / detach choice from the shared
   * `<app-vm-connection-template-attach>` control. Mutates the local class entity in place
   * (so subsequent reads in this view stay consistent) and emits `subscriptionsChanged` so
   * the parent re-fetches the canonical document.
   */
  onVmConnectionTemplateChange(newId: string | null): void {
    if (!this.classId) return;
    this.isSavingVmConnectionTemplate = true;
    this.classService.assignVmConnectionTemplate(this.classId, newId).subscribe({
      next: () => {
        // Optimistically update the locally-held class entity so the attach control reflects
        // the new state without waiting for the parent's silent re-fetch.
        if (this.classEntity) {
          (this.classEntity as any).vmConnectionTemplateId = newId;
        }
        this.isSavingVmConnectionTemplate = false;
        this.notification.notify(
          newId ? 'Connection template attached.' : 'Connection template detached.',
          'success', 2500);
        this.snapshotDone.emit(); // re-uses the existing parent re-fetch hook
      },
      error: err => {
        this.isSavingVmConnectionTemplate = false;
        this.notification.notify(
          err?.error?.message ?? 'Failed to update connection template.', 'error', 3000);
      },
    });
  }
}
